import { FC, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useParams,
} from "react-router-dom";
import HomePage from "./components/HomePage";
import JoinIn from "./components/JoinIn";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import RefundPolicy from "./components/RefundPolicy";
import Login from "./components/Login";
import LoginEmail from "./components/LoginEmail";
import Footer from "./components/Footer";
import MyPage from "./components/MyPage";
import InquiryBoard from "./components/InquiryBoard";
import InquiryDetail from "./components/InquiryDetail";
import InquiryCreate from "./components/InquiryCreate";
import TaskManagement from "./components/TaskManagement";
import AddTask from "./components/AddTask";
import EditTask from "./components/EditTask";
import HowToUse from "./components/HowToUse";
import Download from "./components/Download";
import FeaturesPage from "./components/FeaturesPage";
import Pricing from "./components/Pricing";
import FAQPage from "./components/FAQPage";
import SupportPage from "./components/SupportPage";
import SupportChatbot from "./components/SupportChatbot";
import AIContentPolicy from "./components/AIContentPolicy";
import SampleVideosPage from "./components/SampleVideosPage";
import AdminMembersPage from "./components/AdminMembersPage";
import AdminKnowledgePage from "./components/AdminKnowledgePage";
import AdminBlogPage from "./components/AdminBlogPage";
import YouTubeRankingsPage from "./components/YouTubeRankingsPage";
import BlogIndexPage from "./components/BlogIndexPage";
import BlogPostPage from "./components/BlogPostPage";
import UseCasePage from "./components/UseCasePage";
import UpdateNotesPage from "./components/UpdateNotesPage";
import theme from "./theme";
import { ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import UserStatus from "./components/UserStatus";
import SeoManager from "./components/SeoManager";
import FloatingSupportChat from "./components/FloatingSupportChat";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { getLanguagePreference } from "./utils/userPreferences";
import NotFoundPage from "./components/NotFoundPage";
import {
  isSupportedLanguage,
  normalizeLanguageCode,
} from "./supportedLanguages";

const isRtlLanguage = (languageCode: string): boolean => {
  const normalized = languageCode.toLowerCase();
  return (
    normalized.startsWith("ar") ||
    normalized.startsWith("he") ||
    normalized.startsWith("fa") ||
    normalized.startsWith("ur")
  );
};

const DefaultHomeRoute: FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const savedLanguage = normalizeLanguageCode(getLanguagePreference());
    if (isSupportedLanguage(savedLanguage) && savedLanguage !== "en") {
      navigate(`/${savedLanguage}`, { replace: true });
      return;
    }

    if (i18n.language !== "en") {
      i18n.changeLanguage("en");
    }
  }, [i18n, navigate]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.documentElement.setAttribute("dir", "ltr");
    document.body.setAttribute("dir", "ltr");
  }, []);

  return (
    <>
      <SeoManager lang="en" />
      <HomePage />
    </>
  );
};

const LanguageRoute: FC = () => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const supportedLanguage = isSupportedLanguage(lang);

  useEffect(() => {
    if (supportedLanguage && lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, supportedLanguage]);

  useEffect(() => {
    const effectiveLanguage = supportedLanguage
      ? lang ?? i18n.resolvedLanguage ?? i18n.language
      : "en";
    if (!effectiveLanguage) {
      return;
    }

    const direction = isRtlLanguage(effectiveLanguage) ? "rtl" : "ltr";
    document.documentElement.setAttribute("lang", effectiveLanguage);
    document.documentElement.setAttribute("dir", direction);
    document.body.setAttribute("dir", direction);
  }, [lang, i18n.language, i18n.resolvedLanguage, supportedLanguage]);

  if (!supportedLanguage || !lang) {
    return (
      <>
        <SeoManager lang={lang ?? "en"} />
        <NotFoundPage />
      </>
    );
  }

  return (
    <>
      <SeoManager lang={lang ?? "en"} />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/joinIn" element={<JoinIn />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/ai-content-policy" element={<AIContentPolicy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login-email" element={<LoginEmail />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/inquiries" element={<InquiryBoard />} />
        <Route path="/inquiries/new" element={<InquiryCreate />} />
        <Route path="/inquiries/:inquiryId" element={<InquiryDetail />} />
        <Route path="/task-management" element={<TaskManagement />} />
        <Route path="/task-management/add" element={<AddTask />} />
        <Route path="/task-management/edit" element={<EditTask />} />
        <Route path="/how-to-use" element={<HowToUse />} />
        <Route path="/download" element={<Download />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/sample-videos" element={<SampleVideosPage />} />
        <Route path="/youtube-rankings" element={<YouTubeRankingsPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/updates" element={<UpdateNotesPage />} />
        <Route path="/updates/:slug" element={<UpdateNotesPage />} />
        <Route path="/ai-shorts-maker" element={<UseCasePage slug="ai-shorts-maker" />} />
        <Route path="/marketing-video-maker" element={<UseCasePage slug="marketing-video-maker" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/admin/members" element={<AdminMembersPage />} />
        <Route path="/admin/knowledge" element={<AdminKnowledgePage />} />
        <Route path="/admin/blog" element={<AdminBlogPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/support/chatbot" element={<SupportChatbot />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="*" element={<NotFoundPage lang={lang} />} />
      </Routes>
    </>
  );
};

const App: FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh", // 화면 전체 높이를 채움
        }}
      >
        <Router>
          <UserStatus />
          <main
            style={{
              flex: 1, // 메인 콘텐츠 영역을 남은 공간에 맞춤
              overflow: "auto", // 스크롤 가능하도록 설정
            }}
            >
            <Routes>
              <Route path="/" element={<DefaultHomeRoute />} />
              <Route path="/:lang/*" element={<LanguageRoute />} />
            </Routes>
          </main>
          <FloatingSupportChat />
          <Footer />
          <CookieConsentBanner />
        </Router>
      </div>
    </ThemeProvider>
  );
};

export default App;
